import { Col, Row, message } from 'antd';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  assignToGroups,
  createDocumentGroup,
  createGroup,
  deleteGroup,
  fetchGroups,
} from '../../../api/groups';
import { updateUser } from '../../../api/users';
import { AddGroupForm } from '../../../components';
import {
  DocsPayload,
  GroupDetails,
  GroupDetailsWithNewOrDeleteUser,
  UserDetails,
} from '../../../constants/types';
import { CustomError } from '../../../utils/customError';
import { getParentOrigin } from '../../../utils/getKeycloakConfig';
import { Title } from '../../styles';

const CreateUserGroup = () => {
  const history = useHistory();
  const intl = useIntl();
  const [loading, setLoading] = useState(false);

  const parentOrigin = getParentOrigin();

  const backToGroupsPage = () => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/usergroups`,
        },
        parentOrigin,
      );
    } else {
      history.push('/groups');
    }
  };

  const onSubmit = async (groupDetails: GroupDetailsWithNewOrDeleteUser) => {
    setLoading(true);
    const { name, description, path, addedUser } = groupDetails;
    const docs: DocsPayload = {
      privacyLevel: 'Tenant',
      path: path!,
      name1: name,
      otherString1: description!,
      dataJson: {
        groupName: name,
        groupDescription: description!,
      },
    };

    delete groupDetails.description;
    delete groupDetails.addedUser;
    delete groupDetails.deleteUser;

    createGroup(groupDetails)
      .then(() => {
        createDocumentGroup(docs.path as string, docs)
          .then(async data => {
            if (data.status.toLowerCase() === 'success') {
              await addOrDeleteUserToGroup(groupDetails, addedUser!);
            }
          })
          .catch((error: any) => {
            // Check if entity store error
            // If yes, delete created keycloak group
            deleteKeycloakGroup(groupDetails);
            message.error(
              new CustomError(
                error?.data?.Data?.Error?.errors[0] ?? error?.data?.errorCode,
              ).message,
            );
          });
      })
      .catch((error: any) => {
        message.error(error?.data?.error ?? error?.data?.errorMessage);
      })
      .finally(() => setLoading(false));
  };

  const deleteKeycloakGroup = (groupDetails: GroupDetails) => {
    fetchGroups().then(groups => {
      const newGroup = groups.find(({ name }) => name === groupDetails.name);
      if (newGroup) deleteGroup(newGroup?.id!);
    });
  };

  const addOrDeleteUserToGroup = async (
    groupDetails: GroupDetails,
    added: {
      account_end_date?: string;
      id: string;
      user_details?: UserDetails;
    }[],
  ) => {
    await fetchGroups()
      .then(async groups => {
        const newGroup = groups.find(({ name }) => name === groupDetails.name);

        // Create an array of promises for user operations
        const userPromises = added.map(
          async ({ id, account_end_date, user_details }) => {
            try {
              await assignToGroups(id, newGroup?.id!);
              if (groupDetails.name === 'tenant-admin' && account_end_date) {
                await updateUser(id, {
                  ...(user_details || {}),
                  attributes: {
                    ...(user_details?.attributes || {}),
                    account_end_date: null,
                  },
                });
              }
            } catch (error: any) {
              message.error(error?.data?.error);
            }
          },
        );

        // Wait for all user operations to complete
        await Promise.all(userPromises);

        message.success(
          `'${groupDetails.name}' ${intl.formatMessage({
            id: 'UserGroup.groupName.successfullyAdded',
          })}`,
        );

        if (parentOrigin && window.top && window.top !== window) {
          window.top.postMessage(
            {
              type: 'redirect',
              url: `/admin/usergroups?newGroup=${newGroup?.id}`,
            },
            parentOrigin,
          );
        } else {
          history.push(`/groups?newGroup=${newGroup?.id}`);
        }
      })
      .catch((error: any) => {
        message.error(error?.data?.error);
      });
  };

  return (
    <>
      <Row justify="center" style={{ paddingTop: '28px' }}>
        <Col lg={14} md={18} sm={24} style={{ padding: '20px 30px' }}>
          <Title data-testid="create-group-title">
            <FormattedMessage id="UserGroup.title.addUserGroup" />
          </Title>

          <AddGroupForm
            loading={loading}
            onSubmit={onSubmit}
            cancel={backToGroupsPage}
          />
        </Col>
      </Row>
    </>
  );
};

export default CreateUserGroup;
